import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'
import {
  RuleContext,
  intelligentUpsellFilterRules,
  menuGroupsInCartFilter,
  notInCartFilter,
  upsellSortRules
} from 'public/components/default_template/online_ordering/upsell/utils/upsellRules'
import {
  filterCartUpsellInfo,
  getItemsFlat,
  UpsellItemsMapType,
  usePersistEqualObject
} from 'public/components/default_template/online_ordering/upsell/utils/upsellUtils'
import cloneDeep from 'lodash/cloneDeep'
import { useModal } from 'shared/components/common/modal'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { UpsellItem } from 'public/components/default_template/online_ordering/upsell/UpsellItem'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useOOMenus } from 'public/components/online_ordering/useOOMenu'
import { Channel } from 'public/js/siteUtilities'
import { ItemSource } from 'public/components/default_template/online_ordering/cart/cartUtils'
import { useAddToCartHandler } from 'public/components/default_template/online_ordering/cart/useAddToCartHandler'
import { REQUEST_FAILURE_MESSAGE } from 'shared/js/constants'
import { reportError } from 'src/lib/js/clientError'
import {
  AlertModalSource,
  useAlertModalContext
} from 'shared/components/common/alert_modal/AlertModal'
import { onItemClickImpl } from 'public/components/default_template/menu_section/MenuItemCard'
import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'

export interface SelectedUpsellItem {
  itemGuid: string
  itemGroupGuid: string
}

export type UpsellContextType = {
  upsells: UpsellItem[]
  triggerUpsellModal: (quantity: number) => void
  upsellModalOpen: boolean
  closeUpsellModal: () => void
  addedToCartQuantity: number
  views: number
  maxUpsellModalViews: number
  selectedUpsellItem: SelectedUpsellItem | null
  setSelectedUpsellItem: (selectedUpsellItem: SelectedUpsellItem | null) => void
  handleUpsellItemClicked: (
    item: UpsellItem,
    openItemModal: () => void,
    itemSource: ItemSource
  ) => void
}

export const UpsellContext = createContext<UpsellContextType | undefined>(
  undefined
)

export const MAX_UPSELL_MODAL_VIEWS = 2

// Copied from: https://github.com/toasttab/takeout-web/blob/main/client/components/Upsells/UpsellsProvider.jsx
export const UpsellContextProvider = (props: React.PropsWithChildren<{}>) => {
  const [views, setViews] = useState(0)
  const [selectedUpsellItem, setSelectedUpsellItem] =
    useState<SelectedUpsellItem | null>(null)
  const [addedToCartQuantity, setAddedToCartQuantity] = useState(0)
  const { cart, cartGuid, channel } = useCart()
  const {
    isOpen: upsellModalOpen,
    onOpen: openUpsellModal,
    onClose: closeUpsellModal
  } = useModal()
  const { fulfillmentData } = useFulfillment()
  const { orderPath, ecommPath } = useRestaurantRoutes()
  const path = channel === Channel.ECOMM && ecommPath ? ecommPath : orderPath

  const triggerUpsellModal = useCallback(
    (quantity: number) => {
      setViews((prev) => prev + 1)
      setAddedToCartQuantity(quantity)
      openUpsellModal()
    },
    [openUpsellModal]
  )

  const { selectedLocation } = useRestaurant()
  const { openAlertModal } = useAlertModalContext()
  const { track } = useThrottledTracker()
  const { menus: maybeMenu, popularItems: maybePopularItems } = useOOMenus({})
  const { menus, popularItems } = useMemo(() => {
    const _menus = maybeMenu || []
    const _popularItems = maybePopularItems || []
    return { menus: _menus, popularItems: _popularItems }
  }, [maybeMenu, maybePopularItems])

  const cartUpsellItems = useMemo(() => {
    if (cart) {
      const _cartUpsellItems: string[] = cart?.cartUpsellInfo?.upsellItems || []
      if (_cartUpsellItems.length) {
        return _cartUpsellItems.reduce((acc, item) => {
          acc[item] = true
          return acc
        }, {} as UpsellItemsMapType)
      }
    }
    return null
  }, [cart])

  const filterRules = usePersistEqualObject(intelligentUpsellFilterRules)
  const sortRules = usePersistEqualObject(upsellSortRules)

  const upsells = useMemo(() => {
    if (selectedLocation.upsells && !selectedLocation.upsells.enabled) {
      return []
    }

    let customUpsells: UpsellItem[] = []
    let customUpsellsGuids: Set<string> = new Set()
    let customUpsellMasterIds: string[] = []

    const items = getItemsFlat(menus)
    const ctx = { cart: cloneDeep(cart), popularItems: cloneDeep(popularItems) }

    if (
      selectedLocation.upsells?.enabled &&
      !selectedLocation.upsells.useDefaults
    ) {
      customUpsellMasterIds = selectedLocation.upsells.upsellMappings
        .filter((mapping) => menuGroupsInCartFilter(ctx, mapping))
        .flatMap((mapping) => mapping.menuItemMasterId)
      customUpsells = items
        .filter((item) => {
          // Filter out duplicates
          if (
            !customUpsellsGuids.has(item.guid) &&
            customUpsellMasterIds.includes(item.masterId)
          ) {
            customUpsellsGuids.add(item.guid)
            return true
          }
          return false
        })
        .filter((item) => notInCartFilter(ctx as RuleContext, item as any))
        .sort(
          (a, b) =>
            customUpsellMasterIds.indexOf(a.masterId) -
            customUpsellMasterIds.indexOf(b.masterId)
        )
    }

    const allFilterRules = [
      filterCartUpsellInfo(cartUpsellItems),
      ...filterRules
    ]

    const filtered = allFilterRules.reduce((acc, rule) => {
      return acc.filter((...args: any) => rule(ctx, ...args))
    }, items)

    const sorted = sortRules.reduce((acc: any, rule: any) => {
      return acc.sort((...args: any) => rule(ctx, ...args))
    }, filtered)

    return [
      ...customUpsells,
      ...(sorted?.filter(
        (item: any) => !customUpsellMasterIds.includes(item.masterId)
      ) || [])
    ]?.slice(0, 3)
  }, [
    cart,
    cartUpsellItems,
    filterRules,
    menus,
    popularItems,
    sortRules,
    selectedLocation
  ])

  const handleAddToCart = useAddToCartHandler()
  const handleCartError = useCallback(
    (error: any) => {
      openAlertModal(
        REQUEST_FAILURE_MESSAGE,
        undefined,
        AlertModalSource.UPSELL
      )
      reportError('Error adding item to cart from quick add.', error)
    },
    [openAlertModal]
  )

  const handleUpsellItemClicked = useCallback(
    async (
      item: UpsellItem,
      openItemModal: () => void,
      itemSource: ItemSource
    ) => {
      setSelectedUpsellItem({
        itemGuid: item.guid,
        itemGroupGuid: item.itemGroupGuid
      })
      if (item.hasModifiers) {
        onItemClickImpl(
          undefined,
          false,
          item,
          track,
          path,
          openItemModal,
          cartGuid,
          fulfillmentData?.cartFulfillmentData.deliveryInfo,
          itemSource
        )
      } else {
        await handleAddToCart(
          {
            groupGuid: item.itemGroupGuid,
            modifier: {
              itemGuid: item.guid,
              itemGroupGuid: item.itemGroupGuid,
              modifierGroups: [],
              price: item.price
            },
            modifierGroups: {},
            isRoot: true,
            masterId: item.masterId,
            action: 'replace' as const,
            price: item.price,
            name: item.name
          },
          1,
          channel === Channel.ECOMM
            ? selectedLocation.ecommMenuChannelGuid ?? ''
            : '',
          itemSource,
          handleCartError
        )
      }
    },
    [
      track,
      path,
      cartGuid,
      fulfillmentData?.cartFulfillmentData.deliveryInfo,
      handleAddToCart,
      channel,
      selectedLocation.ecommMenuChannelGuid,
      handleCartError
    ]
  )

  const context = {
    upsells,
    triggerUpsellModal,
    upsellModalOpen,
    closeUpsellModal,
    addedToCartQuantity,
    views,
    maxUpsellModalViews: MAX_UPSELL_MODAL_VIEWS,
    selectedUpsellItem,
    setSelectedUpsellItem,
    handleUpsellItemClicked
  }
  return (
    <UpsellContext.Provider value={context}>
      {props.children}
    </UpsellContext.Provider>
  )
}

export const useUpsell = () => {
  const context = useContext(UpsellContext)
  if (!context) {
    throw new Error('useUpsell must be used within a UpsellContextProvider')
  }

  return context
}
